import React, { useEffect, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'

import { LaunchIcon } from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'

import Image from 'shared/components/common/Image'
import DropDown from 'shared/components/common/dropdown'
import InputMessage from 'shared/components/common/form_input/InputMessage'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useLocationSorting } from 'shared/js/hooks/useLocationSorting'
import { SiteRestaurantLocation } from 'shared/js/types'
import { sortLocations } from 'shared/utils/addressUtils'

import { dummyAddressValue } from 'public/components/online_ordering/addressUtils'

export const formatAddress = (
  loc: Pick<
    SiteRestaurantLocation,
    | 'address1'
    | 'address2'
    | 'city'
    | 'state'
    | 'zipcode'
    | 'administrativeArea'
  >
): string => {
  const parts = [loc.address1]

  if (loc.address2) {
    parts.push(loc.address2)
  }

  parts.push(loc.city)

  if (loc.administrativeArea && loc.administrativeArea !== dummyAddressValue) {
    parts.push(loc.administrativeArea)
  } else if (loc.state && loc.state !== dummyAddressValue) {
    parts.push(loc.state)
  }

  return parts.join(', ')
}

export const formatAddressLabel = (loc: SiteRestaurantLocation): string =>
  `${loc.address1}${loc.address2 ? `, ${loc.address2}` : ''}`

const getRxNameAndAddress = (loc: SiteRestaurantLocation): string =>
  `${loc.name} - ${formatAddressLabel(loc)}`
const getRxDesignationLabel = (
  showLocationNames: boolean,
  loc: SiteRestaurantLocation
): string =>
  showLocationNames && loc.name ? loc.name : formatAddressLabel(loc)
export const getRxDesignation = (
  showLocationNames: boolean,
  loc: SiteRestaurantLocation
): string => (showLocationNames && loc.name ? loc.name : formatAddress(loc))

type Props = {
  locations?: SiteRestaurantLocation[] | null
  selectedLocation?: SiteRestaurantLocation
  setSelectedLocation: (arg0: string) => void
  withIcon?: boolean
  withBorder?: boolean
  leftAligned?: boolean
  withLaunchIcons?: boolean
  id: string
  sortByDistance?: boolean // If false, sorts alphabetically by name instead
  preSortedLocations?: SiteRestaurantLocation[] | null
}

export const LocationSelector = ({
  locations,
  selectedLocation,
  setSelectedLocation,
  withIcon,
  withBorder,
  leftAligned,
  withLaunchIcons,
  id,
  sortByDistance = true,
  preSortedLocations
}: Props) => {
  const { restaurant: siteRestaurant } = useRestaurant()

  // Always call useLocationSorting at the top level (React Rules of Hooks)
  const { sortedLocations: distanceSortedLocations } = useLocationSorting(
    !preSortedLocations && sortByDistance ? locations : null,
    {
      autoRequestLocation: !preSortedLocations && sortByDistance
    }
  )

  // Use pre-sorted locations if provided, otherwise use the hook result or alphabetical sorting
  const sortedLocations =
    preSortedLocations ||
    (sortByDistance ? distanceSortedLocations : sortLocations(locations))

  const showLocationNames: boolean = !!siteRestaurant?.config.showLocationNames

  const dropDownLabel = (
    <>
      {withIcon ? (
        <Image className='icon' alt='Location logo' src='icons/location.svg' />
      ) : null}
      <div className='selectedLocation' data-testid='selected-location'>
        {selectedLocation
          ? getRxDesignationLabel(showLocationNames, selectedLocation)
          : ''}
      </div>
    </>
  )

  if (!locations?.length || locations.length === 1) {
    return null
  }

  return (
    <DropDown
      describedBy={`${id}-input-message`}
      label={dropDownLabel}
      withBorder={withBorder}
      leftAligned={leftAligned}
      testId={id}
    >
      {({ close }) =>
        sortedLocations
          .filter((loc) => loc.externalId !== selectedLocation?.externalId)
          .map((loc) => {
            return (
              <div
                key={loc.name}
                onClick={() => {
                  close()
                  setSelectedLocation(loc.externalId)
                }}
              >
                {getRxDesignationLabel(showLocationNames, loc)}
                {withLaunchIcons && <LaunchIcon />}
              </div>
            )
          })
      }
    </DropDown>
  )
}

type FormLocationSelectorProps = {
  id: string
  label?: string
  required?: boolean
  warningMessage?: string
  sortByDistance?: boolean // If false, sorts alphabetically by name instead
}

const FormLocationSelector = (props: FormLocationSelectorProps) => {
  const { id, label, required, warningMessage, sortByDistance = true } = props
  const { selectedLocation, locations } = useRestaurant()

  const { sortedLocations: distanceSortedLocations } = useLocationSorting(
    sortByDistance ? locations : null,
    {
      autoRequestLocation: sortByDistance
    }
  )

  // Use distance sorting or alphabetical sorting based on prop
  const sortedLocations = sortByDistance
    ? distanceSortedLocations
    : sortLocations(locations)
  const {
    setValue,
    formState: { errors },
    watch
  } = useFormContext()
  const locName = watch(id)

  const formSelected = sortedLocations?.find((loc) =>
    locName ? locName === getRxNameAndAddress(loc) : false
  )

  useEffect(() => {
    if (selectedLocation) {
      setValue(props.id, getRxNameAndAddress(selectedLocation))
    }
    // execute once, on render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation])

  const setSelectedLocation = useCallback(
    (guid: string) => {
      const loc = sortedLocations?.find((loc) => loc.externalId === guid)
      if (!loc) return
      setValue(props.id, getRxNameAndAddress(loc))
    },
    [props.id, setValue, sortedLocations]
  )

  if (!locations?.length || locations.length === 1) {
    return null
  }

  return (
    <div
      className={classnames(
        'formInput',
        errors[id] && 'error',
        formSelected && 'filled'
      )}
      data-testid='form-location-selector'
    >
      <div className='inputWrapper'>
        <div className='inputElementWrapper'>
          <label htmlFor={id}>
            {label}
            {required ? '*' : ''}
          </label>
          <LocationSelector
            id={id}
            locations={locations}
            selectedLocation={formSelected}
            setSelectedLocation={setSelectedLocation}
            leftAligned
            sortByDistance={sortByDistance}
            preSortedLocations={sortedLocations}
          />
        </div>
        {errors[id] && <Image alt='Warning icon' src='icons/warning-red.svg' />}
      </div>
      <InputMessage id={id} warning={warningMessage} />
    </div>
  )
}

export default FormLocationSelector
